import { useState, useCallback } from 'react'
import { createLogger } from '../utils/logger'

const logger = createLogger('useImageGallery')

/**
 * useImageGallery - Reusable hook for managing image gallery viewer state
 * 
 * Handles:
 * - Selected image state
 * - Gallery navigation (prev/next with wrap-around)
 * - Image viewer open/close
 * 
 * Pattern follows useContactForm architecture from claude.md AIM principles
 * 
 * @param {Array} images - Gallery images array
 * @returns {Object} Gallery state and handlers
 * 
 * @example
 * const {
 *   selectedImage,
 *   selectedImageIndex,
 *   handleImageClick,
 *   handleCloseImage,
 *   handlePreviousImage,
 *   handleNextImage
 * } = useImageGallery(entityGallery)
 */
export function useImageGallery(images = []) {
  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  
  /**
   * Open image viewer with specific image
   */
  const handleImageClick = useCallback((image, index) => {
    logger.debug('Image clicked', { index, imageId: image?.id })
    setSelectedImage(image)
    setSelectedImageIndex(index)
  }, [])
  
  /**
   * Close image viewer
   */
  const handleCloseImage = useCallback(() => {
    logger.debug('Closing image viewer')
    setSelectedImage(null)
  }, [])
  
  /**
   * Navigate to previous image (wraps to end)
   */
  const handlePreviousImage = useCallback(() => {
    if (!images || images.length === 0) return
    
    const newIndex = selectedImageIndex === 0 
      ? images.length - 1 
      : selectedImageIndex - 1
    
    logger.debug('Navigate to previous image', { newIndex, totalImages: images.length })
    setSelectedImageIndex(newIndex)
    setSelectedImage(images[newIndex])
  }, [images, selectedImageIndex])
  
  /**
   * Navigate to next image (wraps to start)
   */
  const handleNextImage = useCallback(() => {
    if (!images || images.length === 0) return
    
    const newIndex = selectedImageIndex === images.length - 1
      ? 0
      : selectedImageIndex + 1
    
    logger.debug('Navigate to next image', { newIndex, totalImages: images.length })
    setSelectedImageIndex(newIndex)
    setSelectedImage(images[newIndex])
  }, [images, selectedImageIndex])
  
  return {
    // State
    selectedImage,
    selectedImageIndex,
    
    // Handlers
    handleImageClick,
    handleCloseImage,
    handlePreviousImage,
    handleNextImage
  }
}
